import {
  addMinutes,
  addMilliseconds,
  differenceInMinutes,
  differenceInMilliseconds,
  format,
  isAfter,
  parse,
  isValid,
  isWithinInterval,
  eachDayOfInterval,
  isEqual
} from 'date-fns';

/**
 * Combina la fecha y la hora en un solo objeto Date
 * @param {Date} date La fecha a combinar
 * @param {Date} combineTime La hora a combinar
 * @returns Un objeto Date con la fecha y la hora combinadas
 */
export function combineDateAndTime(date: Date, time: Date): Date {
  const [combineDate, combineTime] = [new Date(date), new Date(time)];
  return new Date(
    combineDate.getFullYear(),
    combineDate.getMonth(),
    combineDate.getDate(),
    combineTime.getHours(),
    combineTime.getMinutes()
  );
}

/**
 * Construye un array de segmentos de tiempo a partir de un límite inferior y superior y un tamaño de segmento en mintos
 * @param {Date} limInf Límite inferior
 * @param {Date} limSup Límite superior
 * @param {number} segmentMinutesSize Tamaño de cada segmento en minutos
 * @returns {Date[]} Un array de segmentos de tiempo
 */
export function createDateSegments(
  limInf: Date,
  limSup: Date,
  segmentMinutesSize: number
): Date[] {
  const segments: Date[] = [];
  if (isAfter(limInf, limSup)) {
    return segments;
  }
  let current = new Date(limInf);
  let next = new Date(current);
  next.setMinutes(current.getMinutes() + segmentMinutesSize);
  segments.push(current);
  const currentSup = new Date(limSup);
  while (next <= currentSup) {
    segments.push(next);
    current = next;
    next = new Date(current);
    next.setMinutes(current.getMinutes() + segmentMinutesSize);
  }

  return segments;
}

/**
 * Formatea una fecha en un string con formato 24 horas
 * @param {Date} time fecha a convertir
 * @returns {string} hora en formato HH:mm
 */
export function dateToStringTime(date: Date): string {
  return format(date, 'HH:mm');
}

/**
 * Crea una fecha con base en los milisegundos
 * @param {number} mills Los milisegundos
 * @returns {Date} La fecha creada
 */
export function fromMills(mills: number): Date {
  return new Date(mills);
}

/**
 * Obtiene la diferencia en minutos entre dos fechas
 * @param {Date} date1 Fecha 1
 * @param {Date} date2 Fecha 2
 * @returns {number} Diferencia en minutos
 */
export function getMinutesDifference(date1: Date, date2: Date): number {
  return differenceInMinutes(date1, date2);
}

/**
 * Obtiene la diferencia en Milliseconds entre dos fechas
 * @param {Date} date1 Fecha 1
 * @param {Date} date2 Fecha 2
 * @returns {number} Diferencia en Milliseconds
 */
export function getMillisecondsDifference(date1: Date, date2: Date): number {
  return differenceInMilliseconds(date1, date2);
}

/**
 * Convierte un string a un objeto Date
 * @param {string} date string a convertir en formato YYYY-MM-DD
 * @returns Un objeto Date
 */
export function stringToDate(date: string): Date {
  const [year, month, day] = date.split('-').map((part) => parseInt(part, 10));
  return new Date(year, month - 1, day);
}

/**
 * Convierte un string a un objeto Date con hora
 * @param date string a convertir en formato YYYY-MM-DD
 * @param time objeto Date que contiene la hora
 * @returns Un objeto Date con la fecha y la hora combinadas
 */
export function stringToDateWithTime(date: string | Date, time: Date): Date {
  const parsedDate =
    typeof date === 'string'
      ? date.split('-').map((part) => parseInt(part, 10))
      : [date.getFullYear(), date.getMonth() + 1, date.getDate()];
  const [year, month, day] = parsedDate;
  return new Date(year, month - 1, day, time.getHours(), time.getMinutes());
}

/**
 * Convierte un objeto Date a una cadena de texto en formato 'YYYY-MM-DD'.
 *
 * @param date - El objeto Date que se desea convertir a cadena.
 * @returns La fecha formateada como una cadena en formato 'YYYY-MM-DD'.
 */
export function dateToString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Convierte un objeto Date a una cadena de texto en formato 'YYYY-MM-DD HH:mm'.
 *
 * @param {Date} date - El objeto Date que se desea convertir a cadena.
 * @returns {string} La fecha formateada
 */
export function dateWithTimeToString(date: Date): string {
  return format(date, 'yyyy-MM-dd HH:mm');
}

/**
 * Agrega minutos a una fecha
 * @param {Date} date La fecha a la que se le agregarán minutos
 * @param {number} minutes La cantidad de minutos a agregar
 * @returns {Date} La nueva fecha con minutos agregados
 */
export function dateAddMinutes(date: Date, minutes: number): Date {
  return addMinutes(date, minutes);
}

/**
 * Valida si un string cumple con un formato de fecha específico y devuelve un objeto Date si es válido.
 *
 * @param dateString El string que representa la fecha.
 * @param formatString El formato esperado del string (ej: 'yyyy-MM-dd', 'dd/MM/yyyy HH:mm').
 * @returns Un objeto Date si el string es válido y cumple con el formato, o null si no lo es.
 */
export function parseDateFromString(
  dateString: string,
  formatString: string
): Date | null {
  try {
    const parsedDate = parse(dateString, formatString, new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    } else {
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * Agrega millisegundos a una fecha
 * @param {Date} date La fecha a la que se le agregarán millisegundos
 * @param {number} milliseconds La cantidad de millisegundos a agregar
 * @returns {Date} La nueva fecha con millisegundos agregados
 */
export function dateAddMilliseconds(date: Date, milliseconds: number): Date {
  return addMilliseconds(date, milliseconds);
}

/**
 * Verifica si las horas de una fecha está dentro de un rango de horas de fechas

 * @param time Objeto Date que representa la hora a verificar
 * @param startDate Objeto Date que representa la fecha de inicio del rango
 * @param endDate Objeto Date que representa la fecha de fin del rango
 * @returns {boolean} true si la hora está dentro del rango, false en caso contrario
 */
export function timeExistsInDateRange(
  time: Date,
  startDate: Date,
  endDate: Date
): boolean {
  return isWithinInterval(time, {
    start: startDate,
    end: endDate
  });
}

/**
 * Devuelve un array de fechas que representan cada día entre dos fechas dadas.
 * @param {Date} start La fecha de inicio del rango.
 * @param {Date} end La fecha de fin del rango.
 * @returns {Date[]} Un array de objetos Date que representan cada día en el rango.
 */
export function getEachDayOfRange(start: Date, end: Date): Date[] {
  if (isAfter(start, end)) {
    return [];
  }

  return eachDayOfInterval({
    start: new Date(start),
    end: new Date(end)
  });
}

/**
 * Verifica si una fecha es posterior o igual a otra.
 * @param {Date} date1 La primera fecha a comparar.
 * @param {Date} date2 La segunda fecha a comparar.
 * @returns {Boolean} true si date1 es posterior o igual a date2, false en caso contrario.
 */
export function isAfterOrEqualsTo(date1?: Date, date2?: Date): boolean {
  if (!date1) {
    return false;
  }

  if (!date2) {
    return true;
  }

  return isAfter(date1, date2) || isEqual(date1, date2);
}

// =========================================================================
// DÍAS HÁBILES - Legacy: UtilitiesDate.daysDiffHoliDays()
// =========================================================================

/**
 * Verifica si una fecha es día de semana (lunes a viernes).
 * Legacy: UtilitiesDate.isWeekday()
 * 
 * @param date Fecha a verificar
 * @returns true si es día de semana (lun-vie), false si es fin de semana (sáb-dom)
 */
export function isWeekday(date: Date): boolean {
  const dayOfWeek = date.getDay();
  return dayOfWeek !== 0 && dayOfWeek !== 6; // 0 = domingo, 6 = sábado
}

/**
 * Verifica si una fecha está en la lista de festivos.
 * Legacy: UtilitiesDate.isHoliday()
 * 
 * @param date Fecha a verificar
 * @param festivos Set de fechas festivas en formato 'YYYY-MM-DD'
 * @returns true si la fecha es festivo
 */
export function isHoliday(date: Date, festivos: Set<string>): boolean {
  return festivos.has(dateToString(date));
}

/**
 * Calcula días hábiles entre dos fechas excluyendo fines de semana y festivos.
 * 
 * Legacy: UtilitiesDate.daysDiffHoliDays(Date dateStart, List<String> holidays)
 * 
 * La lógica del legacy:
 * 1. Si fechaInicio > fechaFin, retorna 0
 * 2. Recorre desde fechaInicio+1 hasta fechaFin (inclusive)
 * 3. Cuenta solo días de semana (lunes-viernes) que no sean festivos
 * 
 * @param fechaInicio Fecha de inicio (ej: fecha de programación)
 * @param fechaFin Fecha de fin (ej: fecha de cumplimiento / hoy)
 * @param festivos Array de fechas festivas en formato 'YYYY-MM-DD'
 * @returns Número de días hábiles entre las dos fechas
 */
export function calcularDiasHabiles(
  fechaInicio: Date,
  fechaFin: Date,
  festivos: string[] = []
): number {
  // Normalizar a medianoche local y avanzar por día calendario (no por +24 h) para que el
  // conteo no dependa de cambios de hora. Misma regla que el backend (RegistrarCumplimientoUseCase).
  const endDate = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate());

  // Legacy: startMillis += MILLISECONDS_PER_DAY (empieza desde el día siguiente)
  let currentDate = new Date(
    fechaInicio.getFullYear(),
    fechaInicio.getMonth(),
    fechaInicio.getDate() + 1
  );

  let workDays = 0;
  const festivosSet = new Set(festivos.map((festivo) => festivo.trim()));

  // Legacy: if (startMillis > endMillis) return 0 / for (currentMillis <= endMillis; ...)
  while (currentDate <= endDate) {
    // Legacy: isWeekday(currentDate) && !isHoliday(currentDateStr, holidays)
    if (isWeekday(currentDate) && !isHoliday(currentDate, festivosSet)) {
      workDays++;
    }

    currentDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 1
    );
  }

  return workDays;
}

/**
 * Indica si un trámite programado en {@code fechaProgramacion} supera el límite de días
 * hábiles ({@code limiteDias}, LIMIT_DAYS_PROGRAM) a la {@code fechaReferencia} dada
 * (normalmente hoy). Legacy: {@code daysDiffHoliDays(...) > Integer.parseInt(valor)}.
 */
export function superaLimiteDiasHabiles(
  fechaProgramacion: Date,
  fechaReferencia: Date,
  festivos: string[],
  limiteDias: number
): boolean {
  return calcularDiasHabiles(fechaProgramacion, fechaReferencia, festivos) > limiteDias;
}
